/** 5 400 000 ms -> "1h 30m". Durations under a minute read "0m". */
export function formatDuration(ms: number): string {
  const totalMinutes = Math.floor(Math.max(0, ms) / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours}h ${String(minutes).padStart(2, "0")}m` : `${minutes}m`;
}

/** Time since an instant, as "12m", "3h 05m" or "2d 4h". */
export function formatElapsed(since: string | Date, now: Date = new Date()): string {
  const ms = Math.max(0, now.getTime() - new Date(since).getTime());
  const days = Math.floor(ms / 86_400_000);
  if (days > 0) return `${days}d ${Math.floor((ms % 86_400_000) / 3_600_000)}h`;
  return formatDuration(ms);
}

/** "2026-10-13" for the given instant in an IANA time zone. */
export function dayInZone(date: Date, timeZone: string): string {
  // en-CA formats dates as YYYY-MM-DD.
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(date);
}

/** Add whole days to a "YYYY-MM-DD" string. */
export function addDays(day: string, amount: number): string {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + amount);
  return date.toISOString().slice(0, 10);
}

/** "Mon 13" for a "YYYY-MM-DD" string. */
export function shortDay(day: string): string {
  return new Intl.DateTimeFormat(undefined, { weekday: "short", day: "numeric", timeZone: "UTC" }).format(
    new Date(`${day}T00:00:00Z`),
  );
}

/** A local clock time such as "09:41". */
export function formatTime(instant: string | Date): string {
  return new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" }).format(new Date(instant));
}

/** A local date and time such as "13 Oct 2026, 09:41". */
export function formatDateTime(instant: string | Date): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(instant));
}

/** A speed in megabits per second: one decimal below 100 ("4.9", "48.0"), whole above ("312"). */
export function formatMbps(mbps: number): string {
  const value = Math.max(0, mbps);
  return value >= 100 ? String(Math.round(value)) : value.toFixed(1);
}

/** A round trip in whole milliseconds, "<1" when shorter than one. */
export function formatLatency(ms: number): string {
  return ms < 1 ? "<1" : String(Math.round(ms));
}

/** A file size in binary units, as Windows shows it: "512 bytes", "24.0 KB", "31.5 MB", "1.20 GB". */
export function formatBytes(bytes: number): { value: string; unit: string } {
  const n = Math.max(0, Math.round(bytes));
  if (n < 1024) return { value: String(n), unit: n === 1 ? "byte" : "bytes" };
  const units = ["KB", "MB", "GB"] as const;
  let value = n / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i++;
  }
  return { value: value >= 100 ? value.toFixed(0) : value >= 10 ? value.toFixed(1) : value.toFixed(2), unit: units[i]! };
}

/** A local date such as "14 Oct 2026". */
export function formatDate(instant: string | Date): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(instant));
}

/** Percentage of part in whole, 0 to 100, safe when whole is 0. */
export function percent(part: number, whole: number): number {
  return whole <= 0 ? 0 : Math.min(100, Math.round((part / whole) * 100));
}

/** Initials for an avatar: "Asha Rao" -> "AR". */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "?";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}
